import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PencilIcon, 
  RocketLaunchIcon, 
  CogIcon,
  DocumentTextIcon,
  TagIcon,
  FolderIcon,
  ChartBarIcon,
  XMarkIcon,
  ArrowLeftIcon,
  CloudArrowUpIcon,
  PaperAirplaneIcon,
  ExclamationCircleIcon,
  LinkIcon,
  ListBulletIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ShareIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { blogService } from '../../services/blogService';
import { BlogPost, BlogCategory, BlogImage } from '../../types/blog';
import ImageManagement from './ImageManagement';
import RichTextEditor from '../CMS/RichTextEditor';
import InlineRichTextEditor from '../CMS/InlineRichTextEditor';

interface BlogEditorProps {
  postId?: string;
}

const BlogEditor: React.FC<BlogEditorProps> = () => {
  const { id: postId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(postId);

  const [post, setPost] = useState<Partial<BlogPost>>({
    title: '',
    content: '',
    excerpt: '',
    status: 'draft',
    categories: [],
    tags: [],
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: [],
      openGraph: {
        title: '',
        description: '',
        image: '',
      },
      twitterCard: {
        title: '',
        description: '',
        image: '',
      },
    },
  });

  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'content' | 'seo' | 'settings'>('content');
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerMode, setImagePickerMode] = useState<'content' | 'featured'>('content');

  useEffect(() => {
    loadCategories();
    if (isEditing && postId) {
      loadPost(postId);
    }
  }, [isEditing, postId]);

  // Debug: Log post state changes
  useEffect(() => {
    console.log('🔄 BlogEditor: Post state changed:', {
      title: post.title,
      contentLength: post.content?.length || 0,
      excerpt: post.excerpt,
      status: post.status,
      hasCategories: post.categories?.length || 0,
      hasTags: post.tags?.length || 0
    });
  }, [post]);

  const loadCategories = async () => {
    try {
      console.log('🔄 Loading categories for BlogEditor...');
      const response = await blogService.getCategories();
      console.log('📊 Categories response in BlogEditor:', response);
      
      if (response.success && response.data) {
        // Handle nested response structure
        let categoriesData = response.data;
        
        // Check if data is nested (has success property inside)
        if ((categoriesData as any).success !== undefined && (categoriesData as any).data !== undefined) {
          console.log('📦 BlogEditor: Detected nested categories response structure');
          categoriesData = (categoriesData as any).data;
        }
        
        // Ensure categoriesData is an array
        if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
          console.log('✅ Categories loaded in BlogEditor:', categoriesData.length, 'categories');
        } else {
          console.warn('⚠️ Categories data is not an array in BlogEditor:', categoriesData);
          setCategories([]);
        }
      } else {
        console.error('❌ Categories API response failed in BlogEditor:', response);
        setCategories([]); // Ensure categories is always an array
      }
    } catch (err) {
      console.error('❌ Error loading categories in BlogEditor:', err);
      setCategories([]); // Ensure categories is always an array
    }
  };

  const loadPost = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔵 BlogEditor: Loading post with ID:', id);
      const response = await blogService.getBlogPost(id);
      console.log('📦 BlogEditor: API response:', response);
      
      if (response.success && response.data) {
        // Handle nested response structure - API returns { success: true, data: { success: true, data: {...}, message: "..." } }
        // or { success: true, data: {...} } directly
        let blogData = response.data;
        
        // Check if data is nested (has success property inside)
        if ((blogData as any).success !== undefined && (blogData as any).data) {
          console.log('📦 BlogEditor: Detected nested response structure');
          blogData = (blogData as any).data;
        }
        
        console.log('✅ BlogEditor: Post data received:', blogData);
        console.log('   - Title:', blogData.title);
        console.log('   - Content length:', blogData.content?.length || 0);
        console.log('   - Excerpt:', blogData.excerpt);
        console.log('   - Status:', blogData.status);
        console.log('   - Categories:', blogData.categories);
        console.log('   - Tags:', blogData.tags);
        
        // Ensure all required fields are present with proper structure
        const postData: Partial<BlogPost> = {
          ...blogData,
          title: blogData.title || '',
          content: blogData.content || '',
          excerpt: blogData.excerpt || '',
          status: blogData.status || 'draft',
          categories: blogData.categories || [],
          tags: blogData.tags || [],
          featuredImage: blogData.featuredImage || '',
          seo: {
            metaTitle: blogData.seo?.metaTitle || '',
            metaDescription: blogData.seo?.metaDescription || '',
            keywords: blogData.seo?.keywords || [],
            openGraph: {
              title: blogData.seo?.openGraph?.title || '',
              description: blogData.seo?.openGraph?.description || '',
              image: blogData.seo?.openGraph?.image || '',
            },
            twitterCard: {
              title: blogData.seo?.twitterCard?.title || '',
              description: blogData.seo?.twitterCard?.description || '',
              image: blogData.seo?.twitterCard?.image || '',
            },
          },
        };
        
        // Set the post data with proper structure
        setPost(postData);
        console.log('✅ BlogEditor: Post state updated with structured data');
        console.log('   - Structured post:', postData);
      } else {
        console.error('❌ BlogEditor: API response failed:', response);
        setError(response.error?.message || 'Failed to load post');
      }
    } catch (err) {
      console.error('❌ BlogEditor: Error loading post:', err);
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (status?: 'draft' | 'published') => {
    setSaving(true);
    setError(null);

    try {
      // Transform categories to array of strings (API expects string[], not objects)
      const categoriesAsStrings = (post.categories || []).map(cat => {
        if (typeof cat === 'string') return cat;
        if (cat && typeof cat === 'object' && cat.name) return cat.name;
        return '';
      }).filter(cat => cat.length > 0);

      // Build the post data for API (categories must be strings for the API)
      const postData: any = {
        ...post,
        status: status || post.status,
        categories: categoriesAsStrings, // API expects array of strings
      };

      console.log('📤 Saving blog post with data:', {
        title: postData.title,
        status: postData.status,
        categories: postData.categories,
        categoriesType: typeof postData.categories,
      });

      let response;
      if (isEditing && postId) {
        response = await blogService.updateBlogPost(postId, postData);
      } else {
        response = await blogService.createBlogPost(postData);
      }

      if (response.success) {
        navigate('/blog');
      } else {
        setError(response.error?.message || 'Failed to save post');
      }
    } catch (err) {
      setError('Failed to save post');
      console.error('Error saving post:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setPost(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSEOChange = (field: string, value: any) => {
    setPost(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        openGraph: prev.seo?.openGraph || { title: '', description: '', image: '' },
        twitterCard: prev.seo?.twitterCard || { title: '', description: '', image: '' },
        [field]: value,
      },
    }));
  };

  const handleOpenGraphChange = (field: string, value: string) => {
    setPost(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        openGraph: {
          ...prev.seo?.openGraph,
          [field]: value,
        },
        twitterCard: prev.seo?.twitterCard || { title: '', description: '', image: '' },
      },
    }));
  };

  const handleTwitterCardChange = (field: string, value: string) => {
    setPost(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        openGraph: prev.seo?.openGraph || { title: '', description: '', image: '' },
        twitterCard: {
          ...prev.seo?.twitterCard,
          [field]: value,
        },
      },
    }));
  };

  const handleCategoryToggle = (categoryId: string) => {
    const category = categories.find(c => c._id === categoryId);
    if (!category) return;

    setPost(prev => {
      const currentCategories = prev.categories || [];
      const isSelected = currentCategories.some(c => c._id === categoryId);
      
      if (isSelected) {
        return {
          ...prev,
          categories: currentCategories.filter(c => c._id !== categoryId),
        };
      } else {
        return {
          ...prev,
          categories: [...currentCategories, category],
        };
      }
    });
  };

  const handleTagsChange = (tagsString: string) => {
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    handleFieldChange('tags', tags);
  };

  const handleImageInsert = (image: BlogImage) => {
    if (imagePickerMode === 'featured') {
      // Set as featured image - store relative URL only
      // The website will construct the full URL based on its API configuration
      const imageUrl = image.url.startsWith('http') 
        ? image.url 
        : image.url; // Keep relative URL as-is
      handleFieldChange('featuredImage', imageUrl);
      setShowImagePicker(false);
      return;
    }

    // Insert into content as HTML img tag (for RichTextEditor)
    // Construct full URL for display in editor
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
    const fullImageUrl = image.url.startsWith('http') 
      ? image.url 
      : `${apiBaseUrl}${image.url}`;
    const altText = image.altText || image.alt || image.originalName || 'Blog image';
    
    // Insert image as HTML into the content
    const currentContent = post.content || '';
    const imageHtml = `<img src="${fullImageUrl}" alt="${altText}" class="max-w-full h-auto rounded-lg my-4" />`;
    
    // Append image to content (RichTextEditor will handle it)
    handleFieldChange('content', currentContent + imageHtml);
    
    setShowImagePicker(false);
  };

  const openImagePicker = (mode: 'content' | 'featured') => {
    setImagePickerMode(mode);
    setShowImagePicker(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 max-w-md">
          <div className="flex flex-col items-center gap-6">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isEditing ? 'Loading Blog Post...' : 'Preparing Editor...'}
              </h3>
              <p className="text-gray-600">
                {isEditing ? 'Fetching your blog content and settings' : 'Setting up the editor'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Enhanced Header */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <PencilIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
                  </h1>
                  <p className="text-gray-600 text-lg">
                    {isEditing ? 'Update your blog post content and settings' : 'Write and publish your blog post'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/blog')}
                className="px-6 py-3 border-2 border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 font-medium flex items-center gap-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={() => handleSave('draft')}
                disabled={saving}
                className="px-6 py-3 border-2 border-amber-200 bg-amber-50 rounded-xl text-amber-700 hover:bg-amber-100 hover:border-amber-300 transition-all duration-200 font-medium disabled:opacity-50 flex items-center gap-2"
              >
                <CloudArrowUpIcon className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={() => handleSave('published')}
                disabled={saving}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium disabled:opacity-50 shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                {saving ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-lg flex items-center gap-3">
            <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Category Selector - Simple Dropdown */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
              <FolderIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Blog Category *</h3>
              <p className="text-sm text-gray-600">Select the type of blog post</p>
            </div>
          </div>
          
          {/* Simple Category Dropdown */}
          <select
            value={(typeof post.categories?.[0] === 'object' ? post.categories[0]?.name : post.categories?.[0]) || ''}
            onChange={(e) => {
              const selectedCategory = e.target.value;
              if (selectedCategory) {
                // Store just the category name as a string (API expects array of strings)
                handleFieldChange('categories', [selectedCategory]);
              } else {
                handleFieldChange('categories', []);
              }
            }}
            className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 font-medium text-lg"
          >
            <option value="">-- Select a Category --</option>
            <option value="Article">📰 Article</option>
            <option value="Tips">💡 Tips</option>
            <option value="Guide">📖 Guide</option>
            <option value="News">📢 News</option>
            <option value="Travel">✈️ Travel</option>
            <option value="Car Rental">🚗 Car Rental</option>
            <option value="Review">⭐ Review</option>
            <option value="Tutorial">🎓 Tutorial</option>
            <option value="General">📝 General</option>
          </select>
          
          {/* Selected Category Display */}
          {post.categories && post.categories.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-gray-600">Selected:</span>
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                <TagIcon className="w-4 h-4" />
                {typeof post.categories[0] === 'object' ? post.categories[0]?.name : post.categories[0]}
              </span>
            </div>
          )}
        </div>

        {/* Debug Info - Only in development */}
        {process.env.NODE_ENV === 'development' && isEditing && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-bold">🔍</span>
              </div>
              <h3 className="text-lg font-bold text-blue-900">Debug Info</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-blue-700">Post ID:</span>
                <span className="ml-2 text-gray-700">{postId}</span>
              </div>
              <div>
                <span className="font-semibold text-blue-700">Title Loaded:</span>
                <span className="ml-2 text-gray-700">{post.title ? '✅ Yes' : '❌ No'}</span>
              </div>
              <div>
                <span className="font-semibold text-blue-700">Content Length:</span>
                <span className="ml-2 text-gray-700">{post.content?.length || 0} chars</span>
              </div>
              <div>
                <span className="font-semibold text-blue-700">Status:</span>
                <span className="ml-2 text-gray-700">{post.status || 'N/A'}</span>
              </div>
              <div>
                <span className="font-semibold text-blue-700">Categories:</span>
                <span className="ml-2 text-gray-700">{post.categories?.length || 0}</span>
              </div>
              <div>
                <span className="font-semibold text-blue-700">Tags:</span>
                <span className="ml-2 text-gray-700">{post.tags?.length || 0}</span>
              </div>
            </div>
            {(!post.title || !post.content) && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ Warning: Some fields are empty. Check browser console for API response details.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Enhanced Tabs */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-100 bg-gray-50">
            <nav className="flex">
              {[
                { id: 'content', label: 'Content', icon: DocumentTextIcon },
                { id: 'seo', label: 'SEO', icon: RocketLaunchIcon },
                { id: 'settings', label: 'Settings', icon: CogIcon },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex-1 py-4 px-6 font-semibold text-sm transition-all duration-200 border-b-3 flex items-center justify-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-white shadow-sm'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="p-8 space-y-8">
              {/* Title Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <DocumentTextIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-1">
                      Blog Post Title *
                      <span className="ml-2 text-xs text-blue-600 font-normal">✨ Rich Text with Colors</span>
                    </label>
                    <p className="text-sm text-gray-600">
                      This is the main headline that readers will see. You can add colors and formatting!
                    </p>
                  </div>
                </div>
                <InlineRichTextEditor
                  content={post.title || ''}
                  onChange={(value) => handleFieldChange('title', value)}
                  placeholder="e.g., 'How to Choose the Perfect Car for Your Next Trip'"
                />
              </div>

              {/* Excerpt Section */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                    <DocumentTextIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-1">
                      Short Summary (Excerpt)
                      <span className="ml-2 text-xs text-purple-600 font-normal">✨ Rich Text Editor</span>
                    </label>
                    <p className="text-sm text-gray-600">
                      A brief preview of your post (1-2 sentences). This appears in blog lists and search results.
                    </p>
                  </div>
                </div>
                <RichTextEditor
                  content={post.excerpt || ''}
                  onChange={(value) => handleFieldChange('excerpt', value)}
                  placeholder="e.g., 'Discover the key factors to consider when selecting a rental car that perfectly matches your travel needs, budget, and preferences.'"
                  minHeight="100px"
                />
              </div>

              {/* Content Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                    <PencilIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-1">
                      Main Content *
                      <span className="ml-2 text-xs text-green-600 font-normal">✨ Rich Text Editor with Colors</span>
                    </label>
                    <p className="text-sm text-gray-600">
                      Write your full blog post content here. Use the formatting toolbar to style your text, add colors, headings, lists, and more!
                    </p>
                  </div>
                </div>
                
                {/* Image Insert Button */}
                <div className="mb-4 flex items-center gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm border-2 border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 flex items-center gap-2 shadow-sm"
                    onClick={() => openImagePicker('content')}
                    title="Insert image from library"
                  >
                    <PhotoIcon className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-600 font-medium">Insert Image from Library</span>
                  </button>
                  <span className="text-sm text-gray-500">Click to add images to your content</span>
                </div>
                
                <RichTextEditor
                  content={post.content || ''}
                  onChange={(value) => handleFieldChange('content', value)}
                  placeholder="Write your blog post content here... Use the toolbar to format text, add headings, lists, links, and colors!"
                  minHeight="400px"
                  onImageUploadRequest={() => openImagePicker('content')}
                />
                
                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">✨</span>
                    <p className="text-sm font-semibold text-blue-800">
                      Rich Text Editor Features:
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="text-sm text-blue-700 space-y-1">
                      <div>• <strong>Bold</strong>, <em>Italic</em>, <u>Underline</u> text</div>
                      <div>• Text colors - select text and pick a color</div>
                      <div>• Headings (H1, H2, H3) for sections</div>
                      <div>• Bullet and numbered lists</div>
                    </div>
                    <div className="text-sm text-blue-700 space-y-1">
                      <div>• Links - select text and add URL</div>
                      <div>• Text alignment (left, center, right)</div>
                      <div>• Blockquotes for emphasis</div>
                      <div>• Undo/Redo for easy editing</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <div className="p-8 space-y-8">
              {/* SEO Importance Notice */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                      <CheckCircleIcon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-green-800 mb-2 flex items-center gap-2">
                      <RocketLaunchIcon className="w-5 h-5" />
                      SEO helps your blog get found on Google!
                    </h3>
                    <p className="text-green-700 leading-relaxed">
                      Fill out these fields to improve your blog's visibility in search engines and social media. Good SEO means more readers will discover your content.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                {/* Basic SEO - Essential */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                        <MagnifyingGlassIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Essential SEO</h3>
                        <p className="text-sm text-gray-600">Most important fields for search engines</p>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          SEO Title (Meta Title) *
                        </label>
                        <input
                          type="text"
                          value={post.seo?.metaTitle || ''}
                          onChange={(e) => handleSEOChange('metaTitle', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                          placeholder="Best Car Rental Tips for Your Perfect Trip | Tak8"
                          maxLength={60}
                        />
                        <div className="flex justify-between items-center mt-2">
                          <p className="text-sm text-gray-600">
                            📊 This appears as the clickable headline in Google search results
                          </p>
                          <span className={`text-xs px-2 py-1 rounded-full ${(post.seo?.metaTitle?.length || 0) > 60 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                            {post.seo?.metaTitle?.length || 0}/60
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          SEO Description (Meta Description) *
                        </label>
                        <textarea
                          value={post.seo?.metaDescription || ''}
                          onChange={(e) => handleSEOChange('metaDescription', e.target.value)}
                          rows={4}
                          className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 resize-none"
                          placeholder="Discover expert tips for choosing the perfect rental car. Learn about pricing, insurance, and booking strategies to make your next trip smooth and affordable."
                          maxLength={160}
                        />
                        <div className="flex justify-between items-center mt-2">
                          <p className="text-sm text-gray-600">
                            📝 This appears as the description under your title in Google search results
                          </p>
                          <span className={`text-xs px-2 py-1 rounded-full ${(post.seo?.metaDescription?.length || 0) > 160 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                            {post.seo?.metaDescription?.length || 0}/160
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Focus Keywords
                        </label>
                        <input
                          type="text"
                          value={post.seo?.keywords?.join(', ') || ''}
                          onChange={(e) => {
                            const keywords = e.target.value.split(',').map(k => k.trim()).filter(k => k);
                            handleSEOChange('keywords', keywords);
                          }}
                          className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                          placeholder="car rental, travel tips, rental car guide, booking tips"
                        />
                        <p className="text-sm text-gray-600 mt-2">
                          🔍 Main keywords people might search for (separate with commas). Use 3-5 relevant keywords.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media SEO */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                        <ShareIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Social Media</h3>
                        <p className="text-sm text-gray-600">How your blog appears when shared on social platforms</p>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Social Media Title
                        </label>
                        <input
                          type="text"
                          value={post.seo?.openGraph?.title || ''}
                          onChange={(e) => handleOpenGraphChange('title', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                          placeholder="🚗 Ultimate Car Rental Guide: Save Money & Travel Smart!"
                        />
                        <p className="text-sm text-gray-600 mt-2">
                          📘 Title when shared on Facebook, LinkedIn, etc. (can be more engaging than SEO title)
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Social Media Description
                        </label>
                        <textarea
                          value={post.seo?.openGraph?.description || ''}
                          onChange={(e) => handleOpenGraphChange('description', e.target.value)}
                          rows={4}
                          className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 resize-none"
                          placeholder="Get insider secrets for booking the perfect rental car! From hidden fees to insurance tricks, we've got you covered. 💡"
                        />
                        <p className="text-sm text-gray-600 mt-2">
                          💬 Description when shared on social media (can be more casual and engaging)
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Social Media Image URL
                        </label>
                        <input
                          type="url"
                          value={post.seo?.openGraph?.image || ''}
                          onChange={(e) => handleOpenGraphChange('image', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                          placeholder="https://yourdomain.com/images/blog-featured-image.jpg"
                        />
                        <p className="text-sm text-gray-600 mt-2">
                          🖼️ Image that appears when shared (1200x630px recommended). Upload to your media library first.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Twitter Card - Simplified */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-6 border-2 border-cyan-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <DevicePhoneMobileIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Twitter Specific</h3>
                    <p className="text-sm text-gray-600">Optional: Customize how your blog appears on Twitter</p>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-4 border border-cyan-200">
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="customize-twitter"
                      className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 w-4 h-4"
                      onChange={(e) => {
                        if (!e.target.checked) {
                          handleTwitterCardChange('title', '');
                          handleTwitterCardChange('description', '');
                          handleTwitterCardChange('image', '');
                        }
                      }}
                    />
                    <label htmlFor="customize-twitter" className="ml-3 text-sm font-semibold text-gray-700">
                      Customize for Twitter (optional)
                    </label>
                  </div>
                  
                  {(post.seo?.twitterCard?.title || post.seo?.twitterCard?.description || post.seo?.twitterCard?.image) && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Twitter Title
                        </label>
                        <input
                          type="text"
                          value={post.seo?.twitterCard?.title || ''}
                          onChange={(e) => handleTwitterCardChange('title', e.target.value)}
                          className="w-full px-3 py-2 border-2 border-cyan-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          placeholder="Leave empty to use Social Media Title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Twitter Description
                        </label>
                        <textarea
                          value={post.seo?.twitterCard?.description || ''}
                          onChange={(e) => handleTwitterCardChange('description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border-2 border-cyan-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                          placeholder="Leave empty to use Social Media Description"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* SEO Preview */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border-2 border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-gray-600 to-slate-700 rounded-xl flex items-center justify-center">
                    <EyeIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Google Search Preview</h3>
                    <p className="text-sm text-gray-600">See how your blog will appear in Google search results</p>
                  </div>
                </div>
                
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm">
                  <div className="text-blue-600 text-lg hover:underline cursor-pointer font-medium">
                    {post.seo?.metaTitle || post.title || 'Your Blog Post Title'}
                  </div>
                  <div className="text-green-700 text-sm mt-1 font-medium">
                    https://yourdomain.com/blog/{post.title?.toLowerCase().replace(/\s+/g, '-') || 'your-post-slug'}
                  </div>
                  <div className="text-gray-600 text-sm mt-3 leading-relaxed">
                    {post.seo?.metaDescription || post.excerpt || 'Your blog post description will appear here...'}
                  </div>
                </div>
              </div>
            </div>
          )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="p-8 space-y-8">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Categories Section */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 border-2 border-orange-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
                  <FolderIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Categories</h3>
                  <p className="text-sm text-gray-600">Organize your blog post by topic</p>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-orange-200 max-h-64 overflow-y-auto">
                {Array.isArray(categories) && categories.length > 0 ? (
                  <div className="space-y-3">
                    {categories.map((category) => (
                      <label key={category._id} className="flex items-center p-3 rounded-lg hover:bg-orange-50 transition-colors duration-200 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={post.categories?.some(c => c._id === category._id) || false}
                          onChange={() => handleCategoryToggle(category._id)}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 w-4 h-4"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-700">{category.name}</span>
                        {category.description && (
                          <span className="ml-2 text-xs text-gray-500">- {category.description}</span>
                        )}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FolderIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No categories available</p>
                    <p className="text-xs text-gray-400 mt-1">Create categories in the Blog Management section</p>
                  </div>
                )}
              </div>
            </div>

            {/* Featured Image Section */}
            <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl p-6 border-2 border-pink-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
                  <PhotoIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Featured Image</h3>
                  <p className="text-sm text-gray-600">Main image for your blog post</p>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-pink-200">
                {post.featuredImage ? (
                  <div className="space-y-4">
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={post.featuredImage.startsWith('http') ? post.featuredImage : `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}${post.featuredImage}`}
                        alt="Featured"
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => handleFieldChange('featuredImage', '')}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        title="Remove featured image"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => openImagePicker('featured')}
                      className="w-full px-4 py-2 border-2 border-pink-200 rounded-xl hover:bg-pink-50 transition-colors font-medium text-pink-700 flex items-center justify-center gap-2"
                    >
                      <PhotoIcon className="w-5 h-5" />
                      Change Image
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <PhotoIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-4">No featured image selected</p>
                    <button
                      onClick={() => openImagePicker('featured')}
                      className="px-6 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl hover:from-pink-600 hover:to-rose-700 transition-all duration-200 font-medium flex items-center justify-center gap-2 mx-auto"
                    >
                      <PhotoIcon className="w-5 h-5" />
                      Select Featured Image
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tags and Status Row */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Tags */}
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-6 border-2 border-teal-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
                  <TagIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Tags</h3>
                  <p className="text-sm text-gray-600">Add relevant keywords</p>
                </div>
              </div>
              
              <input
                type="text"
                value={post.tags?.join(', ') || ''}
                onChange={(e) => handleTagsChange(e.target.value)}
                className="w-full px-4 py-3 border-2 border-teal-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                placeholder="travel, car rental, tips, guide, vacation"
              />
              <p className="text-sm text-gray-600 mt-2">
                <TagIcon className="w-4 h-4 inline mr-1" />
                Separate tags with commas. Tags help readers find related content.
              </p>
            </div>

            {/* Status */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border-2 border-indigo-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <ChartBarIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Publication Status</h3>
                  <p className="text-sm text-gray-600">Control when your post goes live</p>
                </div>
              </div>
              
              <select
                value={post.status || 'draft'}
                onChange={(e) => handleFieldChange('status', e.target.value)}
                className="w-full px-4 py-3 border-2 border-indigo-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 font-medium"
              >
                <option value="draft">📝 Draft - Not visible to public</option>
                <option value="published">✅ Published - Live on website</option>
                <option value="scheduled">⏰ Scheduled - Publish later</option>
              </select>

              {post.status === 'scheduled' && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Scheduled Publication Date
                  </label>
                  <input
                    type="datetime-local"
                    value={post.scheduledDate ? new Date(post.scheduledDate).toISOString().slice(0, 16) : ''}
                    onChange={(e) => handleFieldChange('scheduledDate', new Date(e.target.value))}
                    className="w-full px-4 py-3 border-2 border-indigo-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    ⏰ Your post will automatically go live at this date and time.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Additional Settings */}
          <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border-2 border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-gray-600 to-slate-700 rounded-xl flex items-center justify-center">
                <CogIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Additional Settings</h3>
                <p className="text-sm text-gray-600">Advanced options for your blog post</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">Comments</h4>
                    <p className="text-sm text-gray-600">Allow reader comments</p>
                  </div>
                  <input
                    type="checkbox"
                    defaultChecked={true}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">Featured</h4>
                    <p className="text-sm text-gray-600">Show on homepage</p>
                  </div>
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">Newsletter</h4>
                    <p className="text-sm text-gray-600">Send to subscribers</p>
                  </div>
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>

        {/* Image Picker Modal */}
        {showImagePicker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
              <div className="p-6 overflow-y-auto max-h-[90vh]">
                <ImageManagement
                  selectionMode={true}
                  onImageSelect={handleImageInsert}
                  onClose={() => setShowImagePicker(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogEditor;